import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, articles, faqData } from '@/mocks/articles';

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const featuredArticles = articles.slice(0, 4);
  const latestArticles = articles.slice(4);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative w-full h-[520px] md:h-[640px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Elegant%20art%20museum%20gallery%20interior%20with%20warm%20lighting%2C%20classical%20paintings%20in%20ornate%20frames%2C%20marble%20sculptures%2C%20soft%20cream%20walls%2C%20scholarly%20atmosphere%2C%20high%20ceilings%2C%20refined%20and%20sophisticated%20setting&width=1920&height=1080&seq=hero-bg-2026&orientation=landscape"
              alt="女性裸体艺术解析 - 艺术画廊室内"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4 md:px-6">
            <h1 className="text-3xl md:text-5xl lg:text-6xl text-white font-heading text-center max-w-4xl leading-tight tracking-wide">
              女性裸体艺术解析
            </h1>
            <p className="mt-5 text-base md:text-lg text-white/80 text-center max-w-2xl leading-relaxed">
              从古典到现代的美学演变 —— 系统梳理东西方绘画、雕塑、摄影中对女性裸体的美学诠释与文化意涵
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link
                to="/category/western-classical"
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium text-sm rounded-md transition-colors duration-200 whitespace-nowrap text-center"
              >
                开始探索
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 bg-white/15 hover:bg-white/25 text-white font-medium text-sm rounded-md transition-colors duration-200 whitespace-nowrap text-center backdrop-blur-sm"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-950">艺术分类</h2>
              <p className="mt-3 text-foreground-600 text-sm max-w-lg mx-auto">
                按艺术门类浏览深度分析文章，探索不同媒介与时代中的女性裸体美学
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group block bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300/60 transition-all duration-300"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={cat.imageUrl}
                      alt={cat.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/15 group-hover:bg-black/25 transition-colors duration-300"></div>
                    <div className="absolute bottom-3 left-4">
                      <span className="px-2.5 py-1 bg-background-50/90 text-foreground-700 text-xs rounded-full">
                        {cat.articleCount} 篇文章
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-heading text-foreground-950 group-hover:text-primary-500 transition-colors duration-200">
                      {cat.name}
                    </h3>
                    <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">
                      {cat.description}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-950">精选文章</h2>
                <p className="mt-2 text-foreground-600 text-sm">编辑推荐的高质量学术分析</p>
              </div>
              <Link
                to="/category/western-classical"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap transition-colors"
              >
                查看全部 →
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {featuredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group flex flex-col sm:flex-row bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300/60 transition-all duration-300"
                >
                  <div className="sm:w-48 h-48 sm:h-auto flex-shrink-0 overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex-1 p-5 flex flex-col justify-between">
                    <div>
                      <span className="text-xs text-accent-500 font-medium bg-accent-100/80 px-2 py-0.5 rounded-full">
                        {article.category}
                      </span>
                      <h3 className="mt-3 text-base font-heading text-foreground-950 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">
                        {article.summary}
                      </p>
                    </div>
                    <div className="mt-4 flex items-center gap-4 text-xs text-foreground-500">
                      <span>{article.author}</span>
                      <span>{article.publishDate}</span>
                      <span>{article.readTime} 分钟阅读</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-950">最新文章</h2>
                <p className="mt-2 text-foreground-600 text-sm">持续更新的深度分析内容</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group block bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300/60 transition-all duration-300"
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <span className="text-xs text-accent-500 font-medium bg-accent-100/80 px-2 py-0.5 rounded-full">
                      {article.category}
                    </span>
                    <h3 className="mt-3 text-base font-heading text-foreground-950 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">
                      {article.summary}
                    </p>
                    <div className="mt-4 flex items-center gap-4 text-xs text-foreground-500">
                      <span>{article.author}</span>
                      <span>{article.readTime} 分钟阅读</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-950">常见问题</h2>
              <p className="mt-3 text-foreground-600 text-sm">关于女性裸体艺术研究的常见疑问解答</p>
            </div>
            <div className="space-y-3">
              {faqData.map((faq, index) => (
                <div
                  key={index}
                  className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden"
                >
                  <button
                    className="w-full flex items-center justify-between px-5 py-4 text-left"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <span className="text-sm font-medium text-foreground-950 pr-4">
                      {faq.question}
                    </span>
                    <i
                      className={`ri-arrow-down-s-line text-lg text-foreground-500 transition-transform duration-200 flex-shrink-0 ${
                        openFaq === index ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-950">订阅我们的通讯</h2>
            <p className="mt-3 text-foreground-600 text-sm">
              获取最新的艺术分析文章和研究动态，不错过任何深度内容
            </p>
            <form
              className="mt-8 flex flex-col sm:flex-row gap-3"
              data-readdy-form
              action="https://readdy.ai/api/form/d9splb58o1vn02ekujr0"
              method="POST"
              onSubmit={function (e) {
                e.preventDefault();
                const form = e.currentTarget;
                const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement);
                if (honeypot && honeypot.value.trim()) {
                  const msg = document.getElementById('newsletter-msg');
                  if (msg) { msg.textContent = '感谢订阅！'; msg.className = 'mt-4 text-sm text-green-600'; }
                  return;
                }
                const fd = new FormData(form);
                fd.delete('phone_alt');
                const urlEncoded = new URLSearchParams();
                fd.forEach((v, k) => { urlEncoded.append(k, v as string); });
                fetch(form.action, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: urlEncoded.toString() })
                  .then(async (r) => {
                    const txt = await r.text();
                    let p;
                    try { p = JSON.parse(txt); } catch (_) { p = {}; }
                    const ok = r.ok && (p?.code === 'OK');
                    const msg = document.getElementById('newsletter-msg');
                    if (msg) {
                      msg.textContent = ok ? '感谢订阅！' : (p?.meta?.message || '提交失败，请重试');
                      msg.className = ok ? 'mt-4 text-sm text-green-600' : 'mt-4 text-sm text-red-500';
                    }
                    if (ok) (form.querySelector('[name="email"]') as HTMLInputElement).value = '';
                  })
                  .catch(() => {
                    const msg = document.getElementById('newsletter-msg');
                    if (msg) { msg.textContent = '网络错误，请重试'; msg.className = 'mt-4 text-sm text-red-500'; }
                  });
              } as unknown as React.FormEventHandler}
            >
              <input
                type="email"
                name="email"
                placeholder="请输入您的邮箱地址"
                required
                className="flex-1 px-4 py-3 text-sm border border-background-300/60 rounded-md bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
              />
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="newsletter-hp"
              />
              <button
                type="submit"
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium text-sm rounded-md transition-colors duration-200 whitespace-nowrap"
              >
                订阅
              </button>
            </form>
            <div id="newsletter-msg"></div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}